import React from "react";
import { Link } from "react-router-dom";
import { PawPrint, Mail } from "lucide-react";
import { CATEGORIES, categoryHref } from "../lib/categories";
import { FOOTER_TOP_CITIES, cityUrl, cityCategoryUrl } from "../lib/cities";
import { PRIVATE_PARKS } from "../data/privateParks";
import { LOCAL_PARTNERS } from "../data/localPartners";

const SUPPORT_EMAIL = "info@pawground.app";

/**
 * Site-wide footer. Three goals:
 *   1) Spread internal link equity to the highest-priority city hubs
 *      and category landing pages (every page on the site gets these
 *      links → strong SEO signal for those hubs).
 *   2) Keep the page-1 nav obvious for users (Home, Search, Guides).
 *   3) Keep contact + brand identity grounded.
 */
const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="mt-20 border-t border-brand-border-warm bg-brand-cream"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid gap-10 md:grid-cols-12">
        {/* Brand block */}
        <div className="md:col-span-4">
          <Link to="/" className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-full bg-brand-forest flex items-center justify-center text-white shadow-warm">
              <PawPrint className="w-5 h-5" strokeWidth={2.4} />
            </span>
            <span className="font-heading text-2xl font-black text-brand-ink tracking-tight">
              Pawground
            </span>
          </Link>
          <p className="text-sm text-brand-ink/70 mt-3 max-w-xs leading-relaxed">
            Find safe, fun, and truly dog-friendly places based on real check-ins from the community.
          </p>
          <a
            data-testid="footer-email-link"
            href={`mailto:${SUPPORT_EMAIL}?subject=Pawground%20feedback`}
            className="inline-flex items-center gap-2 mt-5 text-sm font-semibold text-brand-ink hover:text-brand-forest transition-colors"
          >
            <Mail className="w-4 h-4" />
            {SUPPORT_EMAIL}
          </a>
        </div>

        {/* Explore */}
        <div className="md:col-span-2" data-testid="footer-explore">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-forest mb-3">
            Explore
          </div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" className="text-brand-ink/80 hover:text-brand-forest">Home</Link></li>
            <li>
              <Link
                to="/search"
                data-testid="footer-search-link"
                className="text-brand-ink/80 hover:text-brand-forest"
              >
                Search
              </Link>
            </li>
            <li>
              <Link
                to="/guides"
                data-testid="footer-guides-link"
                className="text-brand-ink/80 hover:text-brand-forest"
              >
                Guides
              </Link>
            </li>
            <li><Link to="/profile" className="text-brand-ink/80 hover:text-brand-forest">My profile</Link></li>
            <li>
              <Link
                to="/suggest"
                data-testid="footer-suggest-link"
                className="text-brand-ink/80 hover:text-brand-forest"
              >
                Suggest a Place
              </Link>
            </li>
          </ul>
          {/* Private parks — small launch listing surface. Intentionally
              kept out of the top nav (per product call) but linked from
              every page's footer so the listing has site-wide internal
              link equity for SEO. */}
          {PRIVATE_PARKS.length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-forest mt-6 mb-3">
                Private parks
              </div>
              <ul className="space-y-2 text-sm">
                {PRIVATE_PARKS.map((p) => (
                  <li key={p.slug}>
                    <Link
                      to={`/private-parks/${p.slug}`}
                      data-testid={`footer-private-park-${p.slug}`}
                      className="text-brand-ink/80 hover:text-brand-forest"
                    >
                      {p.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <Link
                    to="/private-parks/host"
                    data-testid="footer-private-park-host"
                    className="text-brand-forest font-semibold hover:underline"
                  >
                    List your yard →
                  </Link>
                </li>
              </ul>
            </>
          )}
        </div>

        {/* Categories */}
        <div className="md:col-span-3" data-testid="footer-categories">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-forest mb-3">
            Categories
          </div>
          <ul className="space-y-2 text-sm">
            {CATEGORIES.map((c) => (
              <li key={c.key}>
                <Link
                  to={categoryHref(c)}
                  data-testid={`footer-cat-${c.key}`}
                  className="text-brand-ink/80 hover:text-brand-forest"
                >
                  {c.seoLabel}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Top cities */}
        <div className="md:col-span-3" data-testid="footer-top-cities">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-brand-forest mb-3">
            Top cities
          </div>
          <ul className="space-y-2 text-sm">
            {FOOTER_TOP_CITIES.map((c) => (
              <li key={c.slug}>
                <Link
                  to={cityUrl(c)}
                  data-testid={`footer-city-${c.slug}`}
                  className="text-brand-ink/80 hover:text-brand-forest"
                >
                  {c.name}, {c.state}
                </Link>
              </li>
            ))}
          </ul>
          {/* Tampa quick-jump to all 9 categories — extra internal link
              equity to the launch market. */}
          <div className="mt-5 text-[11px] uppercase tracking-[0.18em] font-bold text-brand-forest mb-2">
            Tampa quick links
          </div>
          <ul className="space-y-1.5 text-xs">
            {(FOOTER_TOP_CITIES[0] ? CATEGORIES.slice(0, 5).map((c) => (
              <li key={c.key}>
                <Link
                  to={cityCategoryUrl(FOOTER_TOP_CITIES[0], c.urlSlug)}
                  className="text-brand-ink/70 hover:text-brand-forest"
                >
                  {c.seoLabel} in {FOOTER_TOP_CITIES[0].name}
                </Link>
              </li>
            )) : null)}
          </ul>
        </div>
      </div>

      <div
        data-testid="footer-as-featured-on"
        className="border-t"
        style={{ borderTopColor: "#D4C9B0", borderTopWidth: 1 }}
      >
        {/* "As Featured On" — external badges/awards. Centered band
            sitting between the column grid above and the copyright
            strip below. Anchor opens in a new tab; rel="noopener
            noreferrer" keeps the target site from being able to
            touch our window.opener. */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col items-center text-center">
          <div
            className="font-body"
            style={{
              fontSize: 11,
              color: "#7A7A60",
              textTransform: "uppercase",
              letterSpacing: "1px",
              marginBottom: 8,
            }}
          >
            As Featured On
          </div>
          {/* Badge pair — OpenLaunch + AppBoard. Flex-row with wrap so
              they sit side by side on wide viewports and stack neatly
              on narrow ones (no horizontal overflow). Both images
              normalized to height:40 width:auto so the pair visually
              matches heights regardless of each badge's native SVG
              aspect ratio (OpenLaunch is 202×44, AppBoard is 246×64).
              Each anchor opens in a new tab; OpenLaunch keeps its
              existing rel="noopener noreferrer"; AppBoard uses
              rel="noopener nofollow" because it's a third-party award
              backlink. `loading="lazy"` on both keeps them off the
              initial paint budget. */}
          <div className="flex flex-row items-center justify-center flex-wrap gap-4">
            <a
              href="https://openlaunch.ai/projects/pawground"
              target="_blank"
              rel="noopener noreferrer"
              title="Open Launch Top 2 Daily Winner"
              data-testid="footer-openlaunch-badge"
            >
              <img
                src="https://openlaunch.ai/images/badges/top2-dark.svg"
                alt="Open Launch Top 2 Daily Winner"
                loading="lazy"
                style={{ height: 40, width: "auto", maxWidth: "100%" }}
              />
            </a>
            <a
              href="https://appboard.pro/product/pawground/"
              target="_blank"
              rel="noopener nofollow"
              data-testid="footer-appboard-badge"
            >
              <img
                src="https://api.appboard.pro/api/product/name/pawground/badge.svg?style=race&period=day&key=2026-10-01&layout=compact&theme=dark"
                alt="Pawground — #3 Product of the Day (Oct 1, 2026) on AppBoard"
                loading="lazy"
                style={{ height: 40, width: "auto", maxWidth: "100%" }}
              />
            </a>
          </div>
        </div>
      </div>

      {/* Local Partners — small, understated logo strip. Config lives
          in data/localPartners.js so adding a partner is a one-line
          array append with no Footer edits. Label auto-pluralizes
          ("Proud Local Partner" vs "Local Partners") so a single-
          partner launch state doesn't feel presumptuous. Logos wrap
          on narrow screens (flex-wrap) so a growing list never
          overflows the mobile footer.
          Every anchor opens in a new tab with rel="noopener noreferrer"
          — noopener enforced here, not on the config, so no partner
          entry can accidentally omit it.
          Hidden entirely when the partner list is empty. */}
      {LOCAL_PARTNERS.length > 0 && (
        <div
          data-testid="footer-local-partners"
          className="border-t"
          style={{ borderTopColor: "#D4C9B0", borderTopWidth: 1 }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col items-center text-center">
            <div
              className="font-body"
              style={{
                fontSize: 11,
                color: "#7A7A60",
                textTransform: "uppercase",
                letterSpacing: "1px",
                marginBottom: 8,
              }}
            >
              {LOCAL_PARTNERS.length === 1 ? "Proud Local Partner" : "Local Partners"}
            </div>
            <ul
              className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 list-none p-0 m-0"
              data-testid="footer-local-partners-list"
            >
              {LOCAL_PARTNERS.map((p) => (
                <li key={p.href}>
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={p.name}
                    data-testid={`footer-partner-${p.href}`}
                    className="inline-block opacity-80 hover:opacity-100 transition-opacity"
                  >
                    <img
                      src={p.logoSrc}
                      alt={p.name}
                      loading="lazy"
                      // Constrained height + width:auto keeps every logo
                      // visually consistent regardless of source aspect
                      // ratio. Height tuned so wordmark-style logos
                      // (~2:1) keep secondary text legible while square
                      // badges (~1:1) stay understated. maxWidth caps
                      // extra-wide wordmarks so one logo can't swallow
                      // the row on narrow screens.
                      style={{ height: 44, width: "auto", maxWidth: 160 }}
                    />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="border-t border-brand-border-warm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-brand-ink/60">
          <span className="flex items-center gap-3">
            <span>© {new Date().getFullYear()} Pawground · Made for dog parents.</span>
            <Link to="/privacy" data-testid="footer-privacy-link" className="font-semibold hover:text-brand-forest transition-colors">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="font-semibold hover:text-brand-forest transition-colors">Terms</Link>
          </span>
          <span>Park data via Google Places · Maps via OpenStreetMap</span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
